import { isEevoHost } from '@upright/common/arvo/public-url';
import { CompanySubscriptionModal } from '@upright/ui/common/activities/company-subscription/subscription-modal';
import { ContactFormModal } from '@upright/ui/common/activities/contact-and-feedback/contact-modal';
import { LeadFormModal } from '@upright/ui/common/activities/lead-form/lead-form-modal';
import {
  SearchContextProvider,
  useSearchOpenContextBuilder,
} from '@upright/ui/common/generic/components/bou-tool-header/use-search-open-context';
import { GoogleTagManager } from '@upright/ui/common/generic/components/google-tag-manager';
import { GlobalHotkeys } from '@upright/ui/common/generic/global-hotkeys';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { ViewSettingsModal } from '@upright/ui/common/generic/view-settings/view-settings-modal';
import { tabletOrNarrower } from '@upright/ui/common/generic/layout/breakpoint-utils';
import {
  APP_HEADER_HEIGHT_VAR,
  HEADER_HEIGHT_PX,
  UNIFIED_HEADER_HEIGHT_PX,
} from '@upright/ui/common/generic/layout/layout-constants';
import { useUnifiedShell } from '@upright/ui/views/unified-shell/use-unified-shell';
import { ProfileBackTrailRecorder } from '@upright/ui/views/unified-shell/profile-back-trail';
import { themeGet, useTheme } from '@upright/ui/common/generic/theme/custom-theme';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Toaster } from 'react-hot-toast';
import styled, { css } from 'styled-components';
import _defaultIndexHtmlTitle from '../../../../common/index-html-title';
import { ChatRuntimeProvider } from '../../../chat/chat-runtime-provider';
import { ChatSidePanel } from '../../../chat/chat-side-panel';
import { BouAppLoading } from '../../../common/generic/components/progress/spinners/bou-app-loading';
import { bouTextFontStyle } from '../../../common/generic/content/texts/bou-text/text-style';
import { NetImpactDrillInPanel } from '../../../common/upright/core/impact-lenses/net-impact/drill-in-panel/panel';
import { ContributeModal } from '../../../common/upright/model/input/contributions/contribution-modal';
import { StartMenu } from '../../../common/upright/internal/start-menu';
import { StartMenuPageContextProvider } from '../../../common/upright/internal/start-menu/page-context';
import { useIsMaintenanceMode } from '../context/use-is-maintenance-mode';
import { MainContextProvider, useMainContextBuilder } from './context';
import { CookieConsentMaybe } from './cookie-consent-maybe';
import { HeaderMaybe } from './header-maybe';
import { IEFallback } from './internet-explorer-fallback';
import { RoutesMaybe } from './routes';
import { ValuesSelectorMaybe } from './values-selector-maybe';

const defaultIndexHtmlTitle = _defaultIndexHtmlTitle as string;

export function MainRoot() {
  const maintenanceMode = useIsMaintenanceMode();
  const mainContext = useMainContextBuilder();
  const loggedInUser = useGetLoggedInUser();
  const searchOpenContext = useSearchOpenContextBuilder();
  const { theme } = useTheme();
  const unifiedShell = useUnifiedShell();

  // OA: I Added this ugly logic to suppress "Can't perform a React state update on an unmounted component" warning
  // on route changes. If you have a better idea how to handle that, please fix :) The real place where this should
  // be handled might be in MainContext or smth.
  const [isMounted, setIsMounted] = useState(false);
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    setIsMounted(isMountedRef.current);
  }, []);
  if (!isMounted) {
    return <BouAppLoading />;
  }

  const isIE = window.navigator.userAgent.match(/MSIE|Trident/) !== null;
  if (isIE) {
    return <IEFallback />;
  }

  const onEevoHost = isEevoHost(window.location.hostname);
  const [contactModalOpen, setContactModalOpen, contactFormInitialValues] = mainContext.contactModal;
  const { version, release } = mainContext.modelVersionAndRelease || {};

  if (!maintenanceMode && !version && !release) {
    /**
     * The code further down can manage a missing version or release, but not both, so we show a loading
     * spinner when waiting for one of these to become present, which should always eventually happen.
     *
     * Usually we either have neither or both. However, we can be in a situation with
     * - yes release but no version:
     *   - when coming back after a SSO refresh
     *   - possibly also in some other cases when landing directly to an entity page with some specific URL
     * - no release but yes version
     *   - when changing product data version from the dropdown
     *
     * TODO: Refactor this so that loading means loading, and handle missing both version and release
     * some other way.
     */
    return <BouAppLoading />;
  }

  return (
    <MainContextProvider value={mainContext}>
      {/*
       * `ChatRuntimeProvider` (PLT-4625) owns the single assistant-ui runtime
       * instance for the whole app, so the conversation persists when the user
       * clicks "Expand to full chat" from the side panel and the panel
       * unmounts. Mounted here (inside `MainRoot`) rather than in `AppRoot` so
       * the `RenderApiProfile` short-circuit path doesn't pay for a chat
       * runtime it never uses, and so the chat tooling has access to
       * `MainContext` (logged-in user, model version) the way the route-level
       * mount did. `<ChatSidePanel />` is mounted as a sibling of the routes
       * tree below — it self-gates on its module-level open state.
       */}
      <ChatRuntimeProvider>
        <Main id="outer-content" unifiedTopBar={unifiedShell.enabled}>
          <Helmet titleTemplate={`%s - ${defaultIndexHtmlTitle}`} defaultTitle={`${defaultIndexHtmlTitle}`} />
          {/* Eevo's hosts have no cookie banner: they set no cookies that need consent (`EevoRoutes` in `../app.tsx`). */}
          {!onEevoHost && <CookieConsentMaybe />}
          {/* PLT-4824: app-level global hotkeys (Ctrl+* opens View settings).
              Mounted at the root so the binding works on every route —
              previously the equivalent shortcuts lived inside `BouStatusBar`
              and silently broke on pages without the status bar. */}
          <GlobalHotkeys />
          {/* PLT-6869: records where each unified profile was opened from, for its Back. */}
          {unifiedShell.enabled && <ProfileBackTrailRecorder />}
          {/* PLT-4824: View settings modal. Mounted once here so it can be
              opened from any page without a route change — driven by the
              `viewSettingsModalOpen` redux flag flipped by the global
              hotkey, the user-menu item, and the status-bar item. */}
          <ViewSettingsModal />
          {!onEevoHost && <GoogleTagManager gtmId="GTM-TNNLFGS" loggedInUser={loggedInUser} />}
          <ValuesSelectorMaybe />
          <SearchContextProvider value={searchOpenContext}>
            <StartMenuPageContextProvider>
              <HeaderMaybe />
              <BodyContent id="body-content">
                <PageArea>
                  <Toaster
                    toastOptions={{
                      style: {
                        borderRadius: theme?.radii[2],
                        borderWidth: 1,
                        borderStyle: 'solid',
                        boxShadow: 'none',
                        padding: '4px 8px',
                        fontSize: theme?.fontSizes[1],
                        color: theme?.colors.fg.default,
                        background: theme?.colors.canvas.subtle,
                        borderColor: theme?.colors.border.default,
                      },
                      success: {
                        duration: 4000,
                        style: {
                          color: theme?.colors.success.fg,
                          background: theme?.colors.success.subtle,
                          borderColor: theme?.colors.success.muted,
                        },
                      },
                      error: {
                        style: {
                          color: theme?.colors.danger.fg,
                          background: theme?.colors.danger.subtle,
                          borderColor: theme?.colors.danger.muted,
                        },
                      },
                    }}
                  />
                  <RoutesMaybe />
                </PageArea>
              </BodyContent>
              <ContributeModal />
              <CompanySubscriptionModal />
              <ContactFormModal
                isOpen={contactModalOpen}
                setIsOpen={setContactModalOpen}
                site="platform"
                initialValues={contactFormInitialValues}
              />
              <LeadFormModal />
              <ChatSidePanel />
              {/*
               * PLT-4915: the Net Impact drill-in panel renders in the
               * same right-edge slot as `ChatSidePanel`. Both panels
               * self-gate on their own open state (a module-level
               * `useSyncExternalStore` snapshot), so it's safe to mount
               * them as siblings — at most one is visible at a time,
               * and the chat-tab handoff (click ↑ in the drill-in's
               * Chat tab) explicitly closes the drill-in *before*
               * opening the chat panel so there's never a stacking
               * frame mid-handoff. Order matters only for paint when
               * both are about to be open: `NetImpactDrillInPanel` is
               * listed AFTER `ChatSidePanel` so on the rare race a
               * close-then-open swap visually completes "drill-in on
               * top" rather than "chat on top". In practice the close
               * lands in the same microtask as the open, so neither
               * order is observably different — this is defence in
               * depth.
               */}
              <NetImpactDrillInPanel />
              <StartMenu />
            </StartMenuPageContextProvider>
          </SearchContextProvider>
        </Main>
      </ChatRuntimeProvider>
    </MainContextProvider>
  );
}

/*
 * `unifiedTopBar` publishes the height of whichever fixed bar is on screen, for the legacy frames
 * below to clear (`APP_HEADER_HEIGHT_VAR`). The unified shell's white bar is the design system's
 * 50px and the dark `BouToolHeader` is 48px, so the offset cannot be a constant any more.
 *
 * It is set here rather than nearer the bar because the elements that read it — `BouPageWithFrame`,
 * `EntityTitleBar`, the internal frames — are inside `BodyContent`, which is `HeaderMaybe`'s
 * sibling rather than its child. `Main` is the nearest common ancestor, so it is the lowest point
 * the value can cascade from.
 *
 * Below tablet it reverts, because that is where an opted-in user keeps the dark `MobileHeader`
 * (`header-maybe.tsx`).
 */
const Main = styled.div<{ unifiedTopBar: boolean }>`
  ${props => props.unifiedTopBar && `${APP_HEADER_HEIGHT_VAR}: ${UNIFIED_HEADER_HEIGHT_PX};`}
  ${tabletOrNarrower(css`
    ${APP_HEADER_HEIGHT_VAR}: ${HEADER_HEIGHT_PX};
  `)}
  ${bouTextFontStyle};
  color: ${themeGet('colors.fg.default')};
  div {
    box-sizing: border-box;
  }
  min-height: 100vh;
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

const BodyContent = styled.div`
  flex-grow: 1;
  position: relative;
  // RN: Makes sure fixed MobileHeader doesn't hide content under it
  ${tabletOrNarrower(css`
    padding-top: ${HEADER_HEIGHT_PX};
  `)}
`;

// OA: Don't add spacings here without checking how it affects RenderView.
const PageArea = styled.div`
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;
